import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { setAuthLoading } from "@/components/AuthLoadingOverlay";
import { clearSignupResume, resolveResumeDestination } from "@/lib/signup-resume";

/**
 * Email confirmation links sometimes land on "/" instead of the page the
 * user came from (mail apps open a fresh browser, or the auth provider falls
 * back to the site URL). When that happens we must not strand the user on the
 * marketing page: hand them to /dashboard, which resolves the exact onboarding
 * step from the database rather than from this device's local storage.
 */
export function hasAuthLinkParams(): boolean {
  if (typeof window === "undefined") return false;
  const q = new URLSearchParams(window.location.search);
  const h = new URLSearchParams(
    window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash,
  );
  return Boolean(
    q.get("code") ||
      q.get("token_hash") ||
      q.get("type") ||
      h.get("access_token") ||
      h.get("token_hash") ||
      h.get("type"),
  );
}

export function AuthLinkLanding() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasAuthLinkParams()) return;
    let cancelled = false;
    setAuthLoading(true);

    (async () => {
      // The Supabase client consumes the tokens in the URL on boot; poll
      // briefly until the session lands, then continue the onboarding flow.
      for (let i = 0; i < 20 && !cancelled; i++) {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user) {
          const destination = resolveResumeDestination(
            new URLSearchParams(window.location.search).get("next"),
          );
          // Clean the auth params out of the URL before moving on.
          window.history.replaceState({}, "", window.location.pathname);
          clearSignupResume();
          navigate({ to: destination as never, replace: true });
          return;
        }
        await new Promise((r) => setTimeout(r, 150));
      }
      if (!cancelled) setAuthLoading(false);
    })();

    return () => {
      cancelled = true;
      setAuthLoading(false);
    };
  }, [navigate]);

  return null;
}
